import React, { useState, useEffect } from 'react';
import { EmailSignalEngine } from '../../utils/emailSignalData';

export default function KPIDashboard() {
  const [aging, setAging] = useState({});
  const [latency, setLatency] = useState([]);
  const [escalationFeed, setEscalationFeed] = useState([]);
  const [loops, setLoops] = useState({});

  useEffect(() => {
    setAging(EmailSignalEngine.getAgingSummary());
    setLatency(EmailSignalEngine.getLatencyByActor());
    setEscalationFeed(EmailSignalEngine.getEscalationFeed());
    setLoops(EmailSignalEngine.getLoopDensityByCategory());
  }, []);

  const categories = EmailSignalEngine.CATEGORIES;

  return (
    <div className="space-y-6">
      {/* Thread aging heatmap */}
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Thread aging by category</div>
        </div>
        <div className="p-5">
          <div className="text-body-sm" style={{ display: 'grid', gridTemplateColumns: `repeat(${categories.length + 1}, 1fr)`, gap: 8 }}>
            <div className="font-semibold">Category</div>
            {categories.map(c => (
              <div key={c} className="font-semibold" style={{ textAlign: 'center' }}>{c}</div>
            ))}
            <div className="text-text-muted">P50 (h)</div>
            {categories.map(c => (
              <div key={c} style={{ textAlign: 'center', padding: 8, background: aging[c]?.count ? '#DBEAFE' : '#F3F4F6', borderRadius: 4 }}>
                {aging[c]?.p50 ?? '—'}
              </div>
            ))}
            <div className="text-text-muted">Count</div>
            {categories.map(c => (
              <div key={c} style={{ textAlign: 'center', padding: 8, background: aging[c]?.count ? '#D1FAE5' : '#F3F4F6', borderRadius: 4 }}>
                {aging[c]?.count ?? 0}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Latency variance */}
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Latency variance (actors)</div>
        </div>
        <div className="p-5">
          <div className="space-y-3">
            {latency.map(a => (
              <div
                key={a.actorHash}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 12,
                  background: a.deviation ? '#FEF3C7' : '#F9FAFB',
                  borderRadius: 8,
                  borderLeft: a.deviation ? '4px solid #F59E0B' : '4px solid #E5E7EB',
                }}
              >
                <span className="font-medium">{a.displayName}</span>
                <span className="text-body text-text-muted">
                  Mean: {a.meanHours}h · σ: {a.stdDev}h
                  {a.deviation && <span className="font-semibold" style={{ marginLeft: 8, color: '#B45309' }}>Deviation</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Escalation feed */}
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Escalation signal feed</div>
        </div>
        <div className="p-5">
          {escalationFeed.length === 0 ? (
            <p className="text-text-muted" style={{ textAlign: 'center', padding: 24 }}>No escalation signals in feed.</p>
          ) : (
            <div className="space-y-2">
              {escalationFeed.slice(0, 15).map(e => (
                <div
                  key={e.id}
                  className="text-body"
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: 10,
                    background: '#FEF2F2',
                    borderRadius: 6,
                    borderLeft: '3px solid #EF4444',
                  }}
                >
                  <span className="font-semibold text-red-critical">{e.signalType}</span>
                  <span className="text-text-muted">{e.category}</span>
                  <span className="text-body-sm" style={{ color: '#9CA3AF' }}>{new Date(e.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Loop density */}
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Loop density by category</div>
        </div>
        <div className="p-5">
          <div className="space-y-3">
            {Object.entries(loops).map(([name, data]) => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 140, fontWeight: 500 }}>{name}</div>
                <div style={{ flex: 1, height: 24, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
                  <div
                    style={{
                      width: `${Math.min(100, (data.avgLoops / (data.baseline * 2)) * 100)}%`,
                      background: data.avgLoops > data.baseline ? '#F59E0B' : '#10B981',
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
                <span className="text-body text-text-muted" style={{ minWidth: 80 }}>
                  {data.avgLoops} loops (baseline {data.baseline})
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
